import React, { useCallback } from "react";

interface ResizableDividerHorizontalProps {
  onDragStart?: () => void;
  onDragMove?: (clientY: number) => void;
  onDragEnd?: () => void;
}

/**
 * A horizontally draggable divider component for resizing panels vertically
 */
export default function ResizableDividerHorizontal_Component({
  onDragStart,
  onDragMove,
  onDragEnd
}: ResizableDividerHorizontalProps): JSX.Element {
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    // Set cursor and user-select styles
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";

    // Call onDragStart if provided
    if (onDragStart) {
      onDragStart();
    }

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      if (onDragMove) {
        onDragMove(moveEvent.clientY);
      }
    };

    const handleMouseUp = (): void => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";

      // Call onDragEnd if provided
      if (onDragEnd) {
        onDragEnd();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [onDragStart, onDragMove, onDragEnd]);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (onDragStart) {
      onDragStart();
    }

    const handleTouchMove = (moveEvent: TouchEvent): void => {
      moveEvent.preventDefault(); // Prevent page scroll during drag
      const touch = moveEvent.touches[0];
      if (onDragMove) {
        onDragMove(touch.clientY);
      }
    };

    const cleanup = (): void => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", cleanup);
      document.removeEventListener("touchcancel", cleanup);

      if (onDragEnd) {
        onDragEnd();
      }
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", cleanup);
    document.addEventListener("touchcancel", cleanup);
  }, [onDragStart, onDragMove, onDragEnd]);

  return (
    <div
      className="py-1 flex-shrink-0 cursor-row-resize flex justify-center touch-none"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="h-[3px] w-full bg-gray-300 hover:bg-blue-500 transition-colors duration-150 relative group">
        {/* Extended hover/touch target */}
        <div className="absolute inset-x-0 -top-2 -bottom-2 group-hover:bg-blue-500 group-hover:opacity-20" />
        {/* Pill-shaped drag handle — matches Figma "screen-resize" component (rotated for horizontal) */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[21px] h-[13px] bg-white border border-black/15 rounded-full flex items-center justify-center shadow-sm">
          {/* Grip icon — two horizontal lines */}
          <svg width="9" height="5" viewBox="0 0 9 5" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-400 group-hover:text-blue-500 transition-colors duration-150">
            <path d="M1 1h7M1 4h7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}
